import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {BLOCKS, INLINES} from '@contentful/rich-text-types'
import {
  AnimationProvider,
  Box,
  InlineLink,
  LogoSuite,
  type LogoSuiteLogoBarProps,
  type LogoSuiteProps,
  Text,
} from '@primer/react-brand/esm'

import type {PrimerComponentLogoSuite} from '../../../schemas/contentful/contentTypes/primerComponentLogoSuite'

import {Logo, LogoSuiteMap as internalLogoSuiteMap} from './Logo'

export const LogoSuiteMap = internalLogoSuiteMap

export type ContentfulLogoSuiteProps = {
  component: PrimerComponentLogoSuite
  shouldAnimate?: boolean
  variant?: LogoSuiteProps['variant']
  marquee?: LogoSuiteLogoBarProps['marquee']
  marqueeSpeed?: LogoSuiteLogoBarProps['marqueeSpeed']
  className?: string
  logobarClassName?: string
}

export function ContentfulLogoSuite({
  component,
  shouldAnimate = false,
  variant = component.fields.layoutVariant,
  marquee,
  marqueeSpeed,
  className,
  logobarClassName,
}: ContentfulLogoSuiteProps) {
  const fields = component.fields
  const useMarquee = marquee ?? (fields.marquee === 'slow' || fields.marquee === 'normal')
  const speed = marqueeSpeed ?? (fields.marquee === 'slow' || fields.marquee === 'normal' ? fields.marquee : 'normal')

  return (
    <AnimationProvider visibilityOptions={0.5}>
      <LogoSuite
        className={className}
        align={fields.align || 'center'}
        hasDivider={fields.hasDivider}
        variant={variant}
      >
        <LogoSuite.Heading as={fields.headingLevel} visuallyHidden={Boolean(fields.visuallyHideHeading)}>
          {fields.heading}
        </LogoSuite.Heading>
        {component.fields.description ? (
          <LogoSuite.Description>
            {documentToReactComponents(component.fields.description, {
              renderNode: {
                [BLOCKS.PARAGRAPH]: (_, children) => <Text>{children}</Text>,
                [INLINES.HYPERLINK]: (node, children) => <InlineLink href={node.data.uri}>{children}</InlineLink>,
              },
            })}
          </LogoSuite.Description>
        ) : null}
        <LogoSuite.Logobar
          className={logobarClassName}
          variant={fields.variant || 'emphasis'}
          marqueeSpeed={speed}
          marquee={useMarquee}
        >
          {fields.logos.map(logo =>
            shouldAnimate ? <AnimatedLogo logo={logo} key={logo} /> : <Logo key={logo} name={logo} />,
          )}
        </LogoSuite.Logobar>
      </LogoSuite>
    </AnimationProvider>
  )
}

const AnimatedLogo = ({logo}: {logo: string}) => {
  return (
    <Box animate="slide-in-right">
      <Logo name={logo} />
    </Box>
  )
}
